import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProspectLeadsTable,
  getProspectLeadStatuses,
  updateProspectLeadStatuses,
} from '../../../redux/features/globalSlice';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { CancelButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';

const STATUS_DATE_PICKER = [18, 11, 12, 17, 21];

const LeadStatusUpdate = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { leadStatuses, loading } = useSelector((state) => state.global);
  const handleApiResponse = useHandleApiResponse();
  useEffect(() => {
    dispatch(getProspectLeadStatuses());
  }, [dispatch]);

  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [existingLeads, setExistingLeads] = useState([]);
  const [apiMessage, setApiMessage] = useState('');
  const [createClicked, setCreateClicked] = useState(false);

  const initialValues = {
    statusId: row?.current_status_id || '',
    assessmentDate: row?.assessment_date ? dayjs(row.assessment_date) : null,
    remarks: '',
    leadConfirmationForCreation: null,
  };

  const validationSchema = Yup.object({
    statusId: Yup.number().required('Status is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,

    onSubmit: (values) => {
      const payload = {
        prospectId: row.prospect_lead_id,
        statusId: Number(values.statusId),
        assessmentDate:
          values.assessmentDate && dayjs(values.assessmentDate).isValid()
            ? dayjs(values.assessmentDate).format('YYYY-MM-DD HH:mm:ss')
            : null,

        remarks: values.remarks,
        leadConfirmationForCreation: Number(values.statusId) === 38 ? null : values.leadConfirmationForCreation,
      };
      dispatch(updateProspectLeadStatuses(payload)).then((res) => {
        const response = res?.payload; //get status data

        if (response?.data?.confirmBox) {
          setExistingLeads(response.data.existingLeads);
          setApiMessage(response.message);
          setShowConfirmBox(true);
          return;
        }

        handleApiResponse(res, {
          onSuccess: () => {
            onClose(true);
          },
          onFailed: () => {
            console.log('Status update failed');
          },
        });
      });
    },
  });
  const handleCreateAsNew = () => {
    if (createClicked) return; //prevent multipple clicks
    setCreateClicked(true); //disable button permanently
    const payload = {
      prospectId: row.prospect_lead_id,
      statusId: Number(formik.values.statusId),

      assessmentDate:
        formik.values.assessmentDate && dayjs(formik.values.assessmentDate).isValid()
          ? dayjs(formik.values.assessmentDate).format('YYYY-MM-DD HH:mm:ss')
          : null,
      remarks: formik.values.remarks,
      leadConfirmationForCreation: 1,
    };

    dispatch(updateProspectLeadStatuses(payload)).then((res) => {
      handleApiResponse(res, {
        onSuccess: () => {
          onClose(true);
        },
      });
    });
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack spacing={3} mt={2}>
          {/* STATUS */}

          <TextField
            select
            label="Status"
            name="statusId"
            value={leadStatuses.length ? formik.values.statusId : ''}
            onChange={formik.handleChange}
            fullWidth
          >
            {leadStatuses?.map((item) => (
              <MenuItem key={item.sro_lead_status_id} value={item.sro_lead_status_id}>
                {item.sro_lead_status_name}
              </MenuItem>
            ))}
          </TextField>
          {/* DATE TIME PICKER */}
          {STATUS_DATE_PICKER.includes(Number(formik.values.statusId)) && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DateTimePicker']}>
                <DateTimePicker
                  label="Assessment Date"
                  value={formik.values.assessmentDate || null}
                  onChange={(newValue) => formik.setFieldValue('assessmentDate', newValue)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </DemoContainer>

              <Button
                variant="outlined"
                onClick={() => {
                  formik.setFieldValue('assessmentDate', null);
                  formik.setFieldTouched('assessmentDate', false);
                }}
              >
                Clear Date
              </Button>
            </LocalizationProvider>
          )}

          {/* REMARKS */}
          <TextField
            label="Remarks"
            name="remarks"
            multiline
            rows={3}
            value={formik.values.remarks}
            onChange={formik.handleChange}
            fullWidth
          />

          {showConfirmBox && existingLeads.length > 0 && (
            <Stack spacing={1}>
              <Typography fontWeight="bold">Existing Leads</Typography>

              <Typography color="error">{apiMessage}</Typography>
              {existingLeads.map((lead) => (
                <Stack
                  key={lead.lead_id}
                  sx={{
                    border: '1px solid #ddd',
                    padding: 1,
                    borderRadius: 1,
                  }}
                >
                  <span>Name:{lead.lead_name}</span>
                  <span>Mobile:{lead.lead_mob_no}</span>
                  <span>Email:{lead.lead_email_id}</span>
                </Stack>
              ))}
            </Stack>
          )}
          {loading && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}
          {/* SUBMIT */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <CancelButton action={() => onClose(false)}>Cancel</CancelButton>

            {!showConfirmBox ? (
              <UpdateButton disabled={loading} type="submit">
                {loading ? 'Updating...' : 'Update'}
              </UpdateButton>
            ) : (
              <Button variant="contained" color="primary" onClick={handleCreateAsNew} disabled={createClicked}>
                {createClicked ? 'Created' : 'Create As New'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default LeadStatusUpdate;
